/**
 * SkillFeedbackButton Component
 * Persists across page reloads — shows checkmark if already submitted.
 * Click to edit existing feedback or submit new.
 */

import { useState, useEffect } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Popover } from 'antd';
import api from '@/services/api';
import './SkillFeedbackButton.css';
import { mirrorPlacement } from '@/utils/rtl';

interface SkillOption {
  skill_id: string;
  skill_name: string;
}

interface SkillFeedbackButtonProps {
  messageId: string;
  conversationId: string;
  availableSkills: SkillOption[];
  actualSkills?: string[];
  existingFeedback?: SkillOption[];
  onFeedbackChange?: (messageId: string, skills: SkillOption[]) => void;
}

export function SkillFeedbackButton({
  messageId,
  conversationId,
  availableSkills,
  actualSkills = [],
  existingFeedback,
  onFeedbackChange,
}: SkillFeedbackButtonProps) {
  const { t, direction } = useLanguage();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<SkillOption[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize from existing feedback
  useEffect(() => {
    if (existingFeedback && existingFeedback.length > 0) {
      setSelected(existingFeedback);
      setHasSubmitted(true);
    }
  }, [existingFeedback]);

  const toggleSkill = (skill: SkillOption) => {
    setSelected((prev) =>
      prev.some((s) => s.skill_id === skill.skill_id)
        ? prev.filter((s) => s.skill_id !== skill.skill_id)
        : [...prev, skill]
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await api.post(
        `/conversations/${conversationId}/messages/${messageId}/skill-feedback`,
        { expected_skills: selected.map((s) => ({ skill_id: s.skill_id, skill_name: s.skill_name })) }
      );
      setHasSubmitted(selected.length > 0);
      setOpen(false);
      onFeedbackChange?.(messageId, selected);
    } catch (err) {
      console.error('Failed to save skill feedback:', err);
    } finally {
      setSaving(false);
    }
  };

  if (availableSkills.length === 0) return null;

  const content = (
    <div className="skill-feedback-popover">
      <div className="skill-feedback-header">
        {hasSubmitted ? t('chat.editSkillFeedback') : t('chat.markTriggeringSkills')}
      </div>
      <div className="skill-feedback-desc">
        {t('chat.selectEverySkillThatShould')}
      </div>

      {actualSkills.length > 0 && (
        <div className="skill-feedback-actual" dir="auto">
          {t('chat.usedSkills', { skills: actualSkills.filter(s => !['knowledge_search', 'read_file'].includes(s)).join(', ') || t('agentDebug.none') })}
        </div>
      )}

      <div className="skill-feedback-list">
        {availableSkills.map((skill) => {
          const isSelected = selected.some((s) => s.skill_id === skill.skill_id);
          const wasActual = actualSkills.includes(skill.skill_name);
          return (
            <div
              key={skill.skill_id}
              className={`skill-feedback-option ${isSelected ? 'selected' : ''}`}
              onClick={() => toggleSkill(skill)}
            >
              <div className={`skill-feedback-check ${isSelected ? 'checked' : ''}`}>
                {isSelected && (
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <div className="skill-feedback-option-text">
                <span className="skill-feedback-name" dir="auto">{skill.skill_name}</span>
                {wasActual && <span className="skill-feedback-tag">used</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="skill-feedback-footer">
        <button
          className="skill-feedback-submit"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Saving...' : selected.length === 0 ? t('chat.clearFeedback') : hasSubmitted ? `Update (${selected.length})` : `Submit (${selected.length})`}
        </button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement={mirrorPlacement(direction, 'topRight')}
      overlayClassName="skill-feedback-overlay"
      arrow={false}
    >
      <button
        className={`message-action-btn skill-feedback-btn ${hasSubmitted ? 'submitted' : ''}`}
        title={hasSubmitted ? t('chat.editSkillFeedback') : t('chat.tagExpectedSkills')}
      >
        {hasSubmitted ? (
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        )}
      </button>
    </Popover>
  );
}
